import React, { useEffect, useState } from 'react';
import { SearchBar, Button, Table, Pager, TableRow, TableColumn, Anchor } from '@keplerco/core';
import classNames from 'classnames';
import { DataPoint } from '../../../components/charts/recharts.models';
import { EmptyState } from '../../../components/general/empty-state/empty-state';
import { SkeletonOverlay } from '../../../components/general/loading-state/skeleton-overlay';
import { DownloadIcon } from '../../../design/icons/download.icon';
import { DEFAULT_FILTERING_SEARCH_PARAMS, SORT_BY_OPTIONS } from '../../../library/consts/consts';
import { SearchingSearchParams, SortingSearchParamsDeprecated, PagingSearchParams, FilteringSearchParams, AssessmentSearchParams, PermissionSearchParams } from '../../../models/overmind/search-params';
import { useKeplerNavigate } from '../../../navigation/guards/use-kepler-navigate';
import { PagePath } from '../../../navigation/navigation.enums';
import { useAppActions, useAppState } from '../../../overmind';
import { AnalyticsFilters } from '../../../pages/analytics/analytics-filters/analytics-filters';
import { SortBy } from '../../../components/general/sort-by/sort-by';
import { WidgetCard } from '../../../pages/analytics/analytics-filters/widget-card';
import { SortByOption } from '../../../enums/sort-by-option';
import { SelectedFilterOption } from '../../../pages/analytics/analytics-filters/analytics-filters.models';
import { FiltersOutlet } from '../../../pages/analytics/analytics-filters/filters-outlet';
import { getSelectedFilters, removeFilterOption } from '../../../pages/analytics/analytics-filters/analytics-filters.helpers';
import { IAssessmentsCompetencyMatrixWidgetProps } from './assessments-competency-matrix.models';
import { generateUniqueSkillNames, transformAverageScores, transformScores } from './assessments-competency-matrix.helpers';

export function AssessmentsCompetencyMatrixWidget({ baseSearchParams, filtersOptions }: IAssessmentsCompetencyMatrixWidgetProps): React.ReactElement {
  const keplerNavigate = useKeplerNavigate();

  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useState<SearchingSearchParams & SortingSearchParamsDeprecated & PagingSearchParams>({
    search: undefined,
    sortBy: undefined,
    sortAscending: true,
    page: 1,
    pageSize: 10,
  });
  const [filteringSearchParams, setFilteringSearchParams] = useState<FilteringSearchParams>(DEFAULT_FILTERING_SEARCH_PARAMS);
  const [scoresData, setScoresData] = useState<DataPoint[]>([]);
  const [averageScoresData, setAverageScoresData] = useState<DataPoint[]>([]);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilterOption[]>([]);

  async function getData(requestParams: AssessmentSearchParams & PermissionSearchParams & SearchingSearchParams & FilteringSearchParams & SortingSearchParamsDeprecated & PagingSearchParams) {
    const scores = await actions.getAssessmentCompetencyScores(requestParams);
    const averageScores = await actions.getAssessmentAverageScores({ ...requestParams, page: 1, pageSize: 9999 });
    const overallAverageScore = await actions.getAssessmentAverageSkill(requestParams);

    const uniqueSkillNames = generateUniqueSkillNames(scores?.people);

    setScoresData(transformScores(scores?.people, uniqueSkillNames, companyVariables.useLevels));
    setAverageScoresData(transformAverageScores(averageScores?.averages, overallAverageScore, uniqueSkillNames, companyVariables.useLevels));
    setTotalPages(scores?.totalPages ?? 0);
  }

  useEffect(() => {
    async function initialise() {
      setIsLoading(true);
      await getData({ ...baseSearchParams, ...searchParams, ...filteringSearchParams });
      setIsLoading(false);
    }

    initialise();
  }, [baseSearchParams]);

  return (
    <SkeletonOverlay isLoading={isLoading}>
      <WidgetCard title="Competency matrix" subtitle="A view of competencies across the assessed skills, providing insights into capability distribution and potential skill gaps within the assessment participants." showSkillLevelHelp>
        <div>
          <div style={{ display: 'flex', flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
            <SearchBar
              label="Search"
              loading={isSearching}
              onInput={async event => {
                setIsSearching(true);
                const search = (event.target as HTMLInputElement).value;
                setSearchParams({ ...searchParams, search, page: 1 });
                await getData({ ...baseSearchParams, ...searchParams, ...filteringSearchParams, search, page: 1 });
                setIsSearching(false);
              }}
            />

            <AnalyticsFilters
              departmentOptions={filtersOptions.departmentOptions}
              teamOptions={filtersOptions.teamOptions}
              roleOptions={filtersOptions.roleOptions}
              skillOptions={filtersOptions.skillOptions}
              countryOptions={filtersOptions.countryOptions}
              filteringSearchParams={filteringSearchParams}
              setFilteringSearchParams={setFilteringSearchParams}
              applyFilters={async () => {
                setIsLoading(true);
                const selectedFilters = getSelectedFilters({
                  searchParams: filteringSearchParams,
                  departmentOptions: filtersOptions?.departmentOptions,
                  teamOptions: filtersOptions?.teamOptions,
                  roleOptions: filtersOptions?.roleOptions,
                  skillOptions: filtersOptions?.skillOptions,
                  countryOptions: filtersOptions?.countryOptions,
                });
                setSelectedFilters(selectedFilters);
                await getData({ ...baseSearchParams, ...searchParams, ...filteringSearchParams });
                setIsLoading(false);
              }}
              clearFilters={async () => {
                if (!filteringSearchParams.departmentSlugs && !filteringSearchParams.teamSlugs && !filteringSearchParams.roleSlugs && !filteringSearchParams.skillSlugs && !filteringSearchParams.countryIds) return;
                setIsLoading(true);
                setFilteringSearchParams(DEFAULT_FILTERING_SEARCH_PARAMS);
                await getData({ ...baseSearchParams, ...searchParams, ...DEFAULT_FILTERING_SEARCH_PARAMS });
                setIsLoading(false);
              }}
            />

            <SortBy
              options={SORT_BY_OPTIONS.filter(option => option.value !== SortByOption.MostRecentAssigned && option.value !== SortByOption.RecentlyCompleted && option.value !== SortByOption.RecentlyUpdated)}
              value={searchParams.sortBy}
              onChange={async event => {
                setIsLoading(true);
                const sortBy = event.target.value;
                setSearchParams({ ...searchParams, sortBy });
                await getData({ ...baseSearchParams, ...searchParams, ...filteringSearchParams, sortBy });
                setIsLoading(false);
              }}
            />

            <div style={{ marginLeft: 'auto' }}>
              <Button
                square
                theme="dark"
                type="button"
                isLoading={isDownloading}
                loaderText="Downloading..."
                onClick={async () => {
                  setIsDownloading(true);
                  await actions.getAssessmentCompetencyScoresCSV({ ...baseSearchParams, ...searchParams, ...filteringSearchParams });
                  setIsDownloading(false);
                }}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <DownloadIcon />
                  CSV
                </div>
              </Button>
            </div>
          </div>

          <FiltersOutlet
            selectedFilters={selectedFilters}
            removeFilter={async filter => {
              setIsLoading(true);
              const updatedFilteringParams = removeFilterOption(filteringSearchParams, filter);
              setFilteringSearchParams(updatedFilteringParams);
              setSelectedFilters(prevSelectedFilters => prevSelectedFilters.filter(option => option.slug !== filter.slug));
              await getData({ ...baseSearchParams, ...searchParams, ...updatedFilteringParams });
              setIsLoading(false);
            }}
          />
        </div>

        <Table
          emptyState={<EmptyState title="Hang tight!" subtitle="We are waiting for data." />}
          footerContent={
            <div className={classNames({ invisible: !totalPages || totalPages < 2 })}>
              <Pager
                activePageNumber={searchParams.page ?? 1}
                pageCount={totalPages}
                onPageChange={async page => {
                  if (page === searchParams.page) return;
                  setIsLoading(true);
                  setSearchParams({ ...searchParams, page });
                  await getData({ ...baseSearchParams, ...searchParams, ...filteringSearchParams, page });
                  setIsLoading(false);
                }}
              />
            </div>
          }
        >
          {/* TODO: shouldn't it also be [] if no averageScoresData? */}
          {(!!scoresData ? [...scoresData, ...averageScoresData] : []).map((score, index) => (
            <TableRow key={score.name} id={score.name!}>
              {Object.entries(score)
                .filter(([key]) => key !== 'slug')
                .map(([key, value]) => (
                  <TableColumn
                    key={score.slug}
                    id={key}
                    locked={key === 'name'}
                    label={key === 'name' ? 'Name' : key}
                    heatmap={{
                      score: typeof value === 'number' ? Number(value) : String(value),
                      min: companyVariables.useLevels ? companyVariables.minLevel : 0,
                      max: companyVariables.useLevels ? companyVariables.maxLevel : 100,
                    }}
                    fontWeight={index === scoresData?.length ? '700' : 'inherit'}
                    width={220}
                  >
                    {!!score.slug && index !== scoresData?.length && key === 'name' ? (
                      <Anchor
                        hovertype="opacity"
                        textTransform="none"
                        onClick={event => {
                          event.stopPropagation();
                          const path = PagePath.analyticsAssessmentPerson.replace(':companySlug', companyVariables.slug!).replace(':assessmentSlug', baseSearchParams.assessmentSlug).replace(':personSlug', String(score.slug));
                          keplerNavigate(`${PagePath.analyticsBase}${path}`);
                        }}
                      >
                        {value}
                      </Anchor>
                    ) : (
                      value
                    )}
                  </TableColumn>
                ))}
            </TableRow>
          ))}
        </Table>
      </WidgetCard>
    </SkeletonOverlay>
  );
}
